import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Smartphone, 
  Globe, 
  Bitcoin, 
  Briefcase,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Code,
  Users,
  Zap,
  Shield
} from "lucide-react";
import { Button } from "@/components/ui/button";
import CTABanner from "@/components/CTABanner";

const servicesData = {
  "application-development": {
    icon: Smartphone,
    title: "Application Development",
    subtitle: "Custom Mobile & Enterprise Solutions",
    description: "We create powerful, user-friendly applications that drive business growth and enhance user engagement. Our team specializes in native and cross-platform development for iOS and Android.",
    gradient: "from-blue-600 to-blue-400",
    heroImage: "https://images.unsplash.com/photo-1551650975-87deedd944c3?auto=format&fit=crop&w=1200&q=80",
    features: [
      {
        title: "Custom Mobile Apps",
        description: "Native iOS and Android applications built with the latest technologies for optimal performance.",
        icon: Smartphone
      },
      {
        title: "Enterprise Applications",
        description: "Scalable business solutions that streamline operations and boost productivity.",
        icon: Briefcase
      },
      {
        title: "Cross-Platform Development",
        description: "Cost-effective solutions using React Native and Flutter for multiple platforms.",
        icon: Code
      },
      {
        title: "UI/UX Design",
        description: "Intuitive, beautiful interfaces that enhance user experience and engagement.",
        icon: Users
      }
    ],
    benefits: [
      "Faster time-to-market",
      "Reduced development costs",
      "Seamless user experience",
      "Scalable architecture",
      "24/7 technical support",
      "Regular updates & maintenance"
    ],
    process: [
      { step: 1, title: "Discovery", description: "Understanding your requirements and goals" },
      { step: 2, title: "Design", description: "Creating wireframes and UI/UX mockups" },
      { step: 3, title: "Development", description: "Building the application with agile methodology" },
      { step: 4, title: "Testing", description: "Rigorous QA and user acceptance testing" },
      { step: 5, title: "Launch", description: "Deploying to app stores and production" },
      { step: 6, title: "Support", description: "Ongoing maintenance and updates" }
    ]
  },
  "web-development": {
    icon: Globe,
    title: "Web Development",
    subtitle: "Full-Stack Web Solutions",
    description: "From responsive websites to complex web applications, we deliver modern web solutions that help businesses establish a strong online presence and achieve their digital goals.",
    gradient: "from-cyan-500 to-blue-500",
    heroImage: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80",
    features: [
      {
        title: "Full Stack Development",
        description: "End-to-end web development using modern frameworks and technologies.",
        icon: Code
      },
      {
        title: "E-commerce Platforms",
        description: "Custom online stores with secure payment integration and inventory management.",
        icon: Globe
      },
      {
        title: "Responsive Websites",
        description: "Mobile-first designs that look great on all devices and screen sizes.",
        icon: Smartphone
      },
      {
        title: "API Development",
        description: "RESTful and GraphQL APIs for seamless data integration.",
        icon: Zap
      }
    ],
    benefits: [
      "SEO-optimized structure",
      "Fast loading speeds",
      "Secure & reliable hosting",
      "Content management systems",
      "Analytics integration",
      "Ongoing optimization"
    ],
    process: [
      { step: 1, title: "Analysis", description: "Understanding business requirements" },
      { step: 2, title: "Wireframing", description: "Creating site architecture and layouts" },
      { step: 3, title: "Design", description: "Visual design and branding implementation" },
      { step: 4, title: "Development", description: "Frontend and backend development" },
      { step: 5, title: "Testing", description: "Cross-browser and performance testing" },
      { step: 6, title: "Deployment", description: "Launch and post-launch support" }
    ]
  },
  "cryptocurrency-development": {
    icon: Bitcoin,
    title: "Cryptocurrency Development",
    subtitle: "Blockchain & DeFi Solutions",
    description: "We build secure, scalable blockchain solutions including custom tokens, smart contracts, DeFi platforms, and cryptocurrency wallets. Stay ahead in the decentralized future.",
    gradient: "from-purple-600 to-blue-500",
    heroImage: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?auto=format&fit=crop&w=1200&q=80",
    features: [
      {
        title: "Custom Token Creation",
        description: "ERC-20, BEP-20, and custom tokens on various blockchain networks.",
        icon: Bitcoin
      },
      {
        title: "Smart Contracts",
        description: "Secure, audited smart contracts for various blockchain applications.",
        icon: Code
      },
      {
        title: "DeFi Solutions",
        description: "Decentralized exchanges, lending platforms, and yield farming protocols.",
        icon: Zap
      },
      {
        title: "Secure Wallets",
        description: "Multi-currency wallets with advanced security features.",
        icon: Shield
      }
    ],
    benefits: [
      "Security-first approach",
      "Smart contract auditing",
      "Multi-chain support",
      "Regulatory compliance",
      "Scalable architecture",
      "24/7 monitoring"
    ],
    process: [
      { step: 1, title: "Consultation", description: "Understanding blockchain requirements" },
      { step: 2, title: "Tokenomics", description: "Designing token economics and utility" },
      { step: 3, title: "Development", description: "Smart contract and platform development" },
      { step: 4, title: "Audit", description: "Security audit and vulnerability testing" },
      { step: 5, title: "Deployment", description: "Mainnet deployment and configuration" },
      { step: 6, title: "Maintenance", description: "Ongoing support and updates" }
    ]
  },
  "it-consultancy": {
    icon: Briefcase,
    title: "IT Consultancy",
    subtitle: "Strategic Technology Guidance",
    description: "Our expert consultants help businesses navigate digital transformation, optimize IT infrastructure, and develop technology strategies that drive growth and innovation.",
    gradient: "from-blue-500 to-cyan-400",
    heroImage: "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=1200&q=80",
    features: [
      {
        title: "Business Tech Strategy",
        description: "Aligning technology initiatives with business objectives.",
        icon: Briefcase
      },
      {
        title: "Digital Transformation",
        description: "Modernizing legacy systems and processes for the digital age.",
        icon: Zap
      },
      {
        title: "Cloud Consulting",
        description: "Cloud migration, optimization, and managed services.",
        icon: Globe
      },
      {
        title: "Security Assessment",
        description: "Comprehensive security audits and compliance consulting.",
        icon: Shield
      }
    ],
    benefits: [
      "Expert guidance",
      "Cost optimization",
      "Risk mitigation",
      "Improved efficiency",
      "Scalable solutions",
      "Competitive advantage"
    ],
    process: [
      { step: 1, title: "Assessment", description: "Evaluating current IT landscape" },
      { step: 2, title: "Strategy", description: "Developing transformation roadmap" },
      { step: 3, title: "Planning", description: "Creating detailed implementation plan" },
      { step: 4, title: "Execution", description: "Implementing recommendations" },
      { step: 5, title: "Training", description: "Knowledge transfer and training" },
      { step: 6, title: "Review", description: "Ongoing optimization and support" }
    ]
  }
};

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const service = servicesData[serviceId];

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  return (
    <div className="pt-20" data-testid={`service-detail-${serviceId}`}>
      {/* Hero Section */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-10`} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-[#030712]/80 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <Link 
            to="/services" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-8"
            data-testid="back-to-services"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Services
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6`}>
                <service.icon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-gradient leading-tight mb-4">
                {service.title}
              </h1>
              <p className="text-xl text-blue-400 font-medium mb-6">
                {service.subtitle}
              </p>
              <p className="text-lg text-slate-300 leading-relaxed mb-8">
                {service.description}
              </p>
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-blue-600 text-white rounded-full px-8 py-6 font-semibold hover:bg-blue-500 hover:shadow-[0_0_20px_rgba(59,130,246,0.4)] transition-all group"
                  data-testid="service-get-quote-btn"
                >
                  Get a Quote
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="glass rounded-2xl overflow-hidden">
                <img
                  src={service.heroImage}
                  alt={service.title}
                  className="w-full h-[400px] object-cover"
                />
              </div>
              <div className={`absolute -bottom-4 -right-4 w-32 h-32 bg-gradient-to-br ${service.gradient} rounded-full blur-3xl opacity-30`} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-blue-400 mb-4 block">
              What We Offer
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              Key Features
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-8">
            {service.features.map((feature, index) => (
              <motion.div
                key={index}
                className="glass rounded-2xl p-8 glass-hover"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-4`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-24 sm:py-32 bg-[#0F172A]/50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-blue-400 mb-4 block">
                Why Choose Us
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Benefits of Our {service.title}
              </h2>
              <p className="text-lg text-slate-300 mb-8">
                Partner with GWIT for solutions that deliver real business value and competitive advantage.
              </p>
              <ul className="space-y-4">
                {service.benefits.map((benefit, index) => (
                  <motion.li
                    key={index}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    <span className="text-slate-300">{benefit}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className={`glass rounded-2xl p-8 sm:p-12 relative overflow-hidden`}>
                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-5`} />
                <div className="relative text-center">
                  <div className="text-6xl sm:text-7xl font-black text-blue-400 mb-4">100%</div>
                  <div className="text-xl text-white font-semibold mb-2">Client Satisfaction</div>
                  <div className="text-slate-400">Our commitment to excellence</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-blue-400 mb-4 block">
              Our Process
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              How We Work
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.process.map((step, index) => (
              <motion.div
                key={index}
                className="glass rounded-2xl p-6 glass-hover relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-5xl font-black text-blue-600/20 mb-2">
                  0{step.step}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-slate-400">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
};

export default ServiceDetailPage;
